import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { map, mergeMap, Observable, tap } from 'rxjs';
import { ArcGisJobService } from './arcgis-job.service';
import { AuthService } from './auth.service';

interface ExportOptions {
  fields: Partial<__esri.Field>[];
  fileName?: string;
}

export interface FeaturesExportOptions extends ExportOptions {
  features: Partial<{ attributes: any; geometry: __esri.Geometry }>[];
}

export interface LayersExportOptions extends ExportOptions {
  layers: string[];
  where?: string;
  geometry?: __esri.Geometry;
}

@Injectable()
export class ExportService {
  constructor(
    private arcgisJobService: ArcGisJobService,
    private authService: AuthService
  ) {}

  exportAsXlsxLink(
    options: FeaturesExportOptions | LayersExportOptions
  ): Observable<string> {
    return this._exportInternalAsLink('xlsx', options);
  }

  exportAsKmlLink(
    options: FeaturesExportOptions | LayersExportOptions
  ): Observable<string> {
    return this._exportInternalAsLink('kml', options);
  }

  exportAsShpLink(
    options: FeaturesExportOptions | LayersExportOptions
  ): Observable<string> {
    return this._exportInternalAsLink('shp', options);
  }

  private _exportInternalAsLink(
    fileType: string,
    options: FeaturesExportOptions | LayersExportOptions
  ) {
    return this.authService.getEsriToken().pipe(
      mergeMap((token) => {
        const formData = new FormData();
        formData.append('f', 'json');
        formData.append('fileType', fileType);
        formData.append('public', token ? 'false' : 'true');

        let observable: Observable<any>;

        if ((options as FeaturesExportOptions).features) {
          const auxOptions = options as FeaturesExportOptions;
          formData.append(
            'jsonFeatures',
            JSON.stringify({
              fields: auxOptions.fields,
              features: auxOptions.features,
            })
          );

          observable = this.arcgisJobService.executeJobAndReturnResult(
            '~exportService',
            formData,
            'outputFile'
          );
        }

        if ((options as LayersExportOptions).layers) {
          const auxOptions = options as LayersExportOptions;
          formData.append(
            'inputsParameters',
            JSON.stringify({
              fields: auxOptions.fields?.map((x) => x.name),
              layers: auxOptions.layers,
              where: auxOptions.where ?? '1=1',
              spatialFilter: auxOptions.geometry,
            })
          );

          observable = this.arcgisJobService.executeJobAndReturnResult(
            '~exportLayersService',
            formData,
            'outputFile'
          );
        }

        if (!observable) {
          throw new Error('Input params is wrong!');
        }

        return observable.pipe(
          map((e) => {
            let url = e?.value ?? '';

            if (url && token) {
              const urlInstance = new URL(url);
              urlInstance.searchParams.set('token', token);
              url = urlInstance.toString();
            }

            return url as string;
          })
        );
      })
    );
  }
}
